import { Field, Input, VStack, Text, Spinner } from '@chakra-ui/react';
import { useAppStore } from '@halo/chat-sdk-react';
import { useDebounceFn } from 'ahooks';
import type { FieldProps } from './helpers';

interface EditUserNameProps extends FieldProps {}

const EditUserName = ({ user, form }: EditUserNameProps) => {
  const { t } = useTranslation();
  const { client } = useAppStore();

  const {
    register,
    formState: { errors },
    clearErrors,
    setError,
  } = form;

  const [checking, setChecking] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const debouncedUsernameCheck = useDebounceFn(
    async (value: string) => {
      try {
        setChecking(true);
        const res = await client?.Api.http.usernameCheck({ username: value.trim() });
        if (res?.code === 1) {
          setErrorMessage('');
          clearErrors('username');
        } else {
          setErrorMessage(res?.message || '');
          setError('username', { message: res?.message || '' });
        }
      } catch (error) {
        setError('username', { message: 'Username check failed' });
        console.error(error);
      } finally {
        setChecking(false);
      }
    },
    { wait: 500 }
  );

  return (
    <Field.Root invalid={!!errors.username || !!errorMessage}>
      <Input
        maxLength={32}
        background="white"
        border="none"
        {...register('username', {
          required: t('profile.enterUsername'),
          validate: () => {
            if (errorMessage) {
              return errorMessage;
            }
            return true;
          },
          onChange: (e) => {
            debouncedUsernameCheck.run(e.target.value);
          },
        })}
        placeholder={t('profile.enterUsername')}
        _placeholder={{ color: 'fg.subtle' }}
        defaultValue={user?.username}
      />
      {checking && (
        <Field.HelperText fontSize="xs" display="flex" alignItems="center" gap={2}>
          <Spinner size="xs" />
          <Text>{t('profile.checkingUsernameTips')}</Text>
        </Field.HelperText>
      )}
      <Field.ErrorText>{(errors?.username?.message as string) || errorMessage}</Field.ErrorText>
      <Field.HelperText fontSize="xs">
        <VStack>
          <Text w="full" color={'fg.muted'}>
            {t('profile.usernameExtra')}
          </Text>
          <Text w="full" color={'fg.muted'}>
            {t('profile.usernameExtra2')}
          </Text>
        </VStack>
      </Field.HelperText>
    </Field.Root>
  );
};

export { EditUserName };
